import {inject, Injectable} from '@angular/core';
import {CommonsService} from '../core/commons.service';
import {AConst} from '../core/a-const.enum';
import {FocusServiceImplementation, SearchFocusService} from './search-focus.service';
import {
  RootSearchView,
  SearchViewPath
} from '../core/definitions/search-objects';
import {QueryFieldMenuService} from './query-field-menu.service';
import {ResultViewService} from './result-view.service';
import {MetaField} from '../core/definitions/meta-field';
import {SearchStateService} from './search-state.service';
import {SearchFilterService} from './search-filter.service';
import {SearchExecutorService} from './search-executor.service';
import {SearchResultSelectionsService} from './search-result-selections.service';
import {SearchFilters} from '../core/definitions/search-filters';
import {UserCacheService} from '../core/user-cache.service';
import {SearchTemplateGroupService} from './search-template-group.service';
import {SearchContainer, SearchContainerParams} from '../core/definitions/search-container';
import {Focus} from '../core/definitions/focus';
import {PrimusRouterService} from '../core/primus-router.service';
import {SearchViewSortOrderMenuService} from "./search-view-sort-order-menu.service";
import {SearchViewFilterService} from "./search-view-filter.service";
import {SearchResultSelectionsSorter} from "../core/search-result-selections-sorter";

export interface SearchMenuPath {
  title: string;
  targetId: string;
  path: string;
}

@Injectable({
  providedIn: 'root'
})
export class SearchHandlerService {
  // Injections
  private readonly commons: CommonsService = inject(CommonsService);
  private readonly primusRouter: PrimusRouterService = inject(PrimusRouterService);
  private readonly queryFieldMenuService: QueryFieldMenuService = inject(QueryFieldMenuService);
  private readonly resultViewService: ResultViewService = inject(ResultViewService);
  private readonly searchExecutorService: SearchExecutorService = inject(SearchExecutorService);
  private readonly searchFilterService: SearchFilterService = inject(SearchFilterService);
  private readonly searchFocusService: SearchFocusService = inject(SearchFocusService);
  private readonly searchResultSelectionsService: SearchResultSelectionsService = inject(SearchResultSelectionsService);
  private readonly searchStateService: SearchStateService = inject(SearchStateService);
  private readonly searchTemplateGroupService: SearchTemplateGroupService = inject(SearchTemplateGroupService);
  private readonly sortOrderMenuService: SearchViewSortOrderMenuService = inject(SearchViewSortOrderMenuService);
  private readonly searchViewFilterService: SearchViewFilterService = inject(SearchViewFilterService);
  private readonly userCacheService: UserCacheService = inject(UserCacheService);
  private readonly searchResultSelectionsSorter: SearchResultSelectionsSorter = inject(SearchResultSelectionsSorter);

  private fsi: FocusServiceImplementation;

  /**
   * @param params Params for creating search container
   * @param.searchViewName Name of search view to retrieve from
   * server.
   * @param.filter Search filters, which will be added to search view
   * filters
   * @param.targetState The target state name that will be used when
   * setting state parameters.
   * @param.stateParams Initial state parameters that will be used
   * to set some search container attributes. Relevant for
   * page reload.
   * @param.targetObject An object that will be associated with the
   * search container. The object can later be accessed directly
   * from the search container object.
   */
  async createSearchContainer(params: SearchContainerParams): Promise<SearchContainer> {
    const filters = params.filters || {};
    const view = await this.getSearchView(params, filters);
    const searchContainer: SearchContainer = new SearchContainer();
    if (view) {
      searchContainer.restrictions = params.restrictions;
      searchContainer.rows = params.rows;
      searchContainer.searchPostFn = params.searchPostFn;
      searchContainer.searchResultViews = view.search_result_views;
      searchContainer.searchView = view;
      searchContainer.state.targetState = params.targetState;
      searchContainer.targetObject = params.targetObject;
      searchContainer.qOp = params.qOp;
      this.searchTemplateGroupService.setTemplateGroup(searchContainer, params.templateGroupId);
      await this.searchTemplateGroupService.setListFieldTemplateGroup(searchContainer, params.listFieldTemplateId);

      searchContainer.used = params.used;
      searchContainer.keepSelected = params.keepSelected;
      this.searchResultSelectionsService.setSelections(searchContainer, params.selectedItems || []);
      this.resultViewService.calculateAndSetsDefaultRowsColumns(searchContainer);
      if (params.getFocus) {
        this.fsi = this.searchFocusService.createFSI(searchContainer);
        await this.fsi.loadSavedFocuses();
        const curFocus = this.setSearchContainerFromFocus(this.fsi, params);
        if (!curFocus) {
          this.resultViewService.setDefaultSearchResultView(searchContainer);
        }
        this.setDefaultSearchContainerProps(searchContainer, params);
        return this.finishSearchContainerCreation(searchContainer, params);
      } else {
        this.resultViewService.setDefaultSearchResultView(searchContainer);
        this.setDefaultSearchContainerProps(searchContainer, params);
        return this.finishSearchContainerCreation(searchContainer, params);
      }
    } else {
      console.warn('No view: ' + params.searchViewName);
    }
  }

  getPathView(path: string, searchContainer: SearchContainer): SearchViewPath {
    let pathView: SearchViewPath;
    const searchView = searchContainer.searchView;
    if (searchView) {
      pathView = this.findPathView(path, searchView);
      if (!pathView) {
        console.warn('Could not find path \'' + path + '\' ' +
          'in menu view');
      }
    } else {
      console.warn('Search view not defined in search container');
    }
    return pathView;
  }

  setOrder(searchContainer: SearchContainer, fieldName: string, fieldInfo?: MetaField) {
    const curOrder = searchContainer.getOrder();
    let orderFieldName = fieldName;
    let parentQuery = '';
    if (fieldInfo && fieldInfo.input_type === AConst.MAP_ID) {
      orderFieldName = fieldName + '_value';
    }
    // Handle ordering on child fields, which are field of objects that are indexed as child documents in Solr and
    // cannot be sorted the usual way. It's a bit shaky due to depending on child object types containing 'Item'.
    if (fieldInfo) {
      const childObjectType = fieldInfo.model_type || fieldInfo.parent_object_type;
      if (childObjectType?.includes('Item')) {
        [orderFieldName, parentQuery] = this.getChildOrderFieldName(searchContainer, childObjectType, orderFieldName);
      }
    }
    if (fieldName.indexOf(' asc') !== -1 || fieldName.indexOf(' desc') !== -1) {
      searchContainer.setOrder(fieldName, false, parentQuery);
    } else if (curOrder.indexOf(orderFieldName) === -1 || curOrder.indexOf(' desc') > 1) {
      searchContainer.setOrder((fieldInfo && fieldInfo.path && !orderFieldName.includes('.')) ?
        fieldInfo.path + '.' + orderFieldName : orderFieldName, false, parentQuery);
    } else {
      searchContainer.setOrder(  `${searchContainer.getOrder()} desc`, false, parentQuery);
    }
    if (searchContainer.getOrder().includes('{random}')) {
      searchContainer.orderRandomSeed = Math.round(Math.random() * 10000).toString();
    }
    this.searchExecutorService.resetSearchPosition(searchContainer);
    this.searchExecutorService.runSearch(searchContainer).then(() => {
      if (searchContainer.selections.selectedItems.length > 1) {
        // The sorting of selected items doesn't update the view automatically, probably must implement signaling
        // towards the
        this.searchResultSelectionsSorter.sortItems(searchContainer);
      }
    });
  }

  async goPathView(path: string, searchContainer: SearchContainer, keepFilterGroups: boolean = false) {
    const paramsSetFromFocus = this.checkSetParamsFromFocus(path, searchContainer);
    searchContainer.path = path;
    this.searchExecutorService.resetSearchPosition(searchContainer);
    searchContainer.searchResult = null;
    const pathView = await this.setCurrentPathView(searchContainer);
    if (!pathView.search_view.override_target) {
      this.resultViewService.setCurrentResultViewFromPathView(searchContainer, pathView);
      this.searchFilterService.resetFilter(searchContainer);
      const sortOrderMenus = await this.sortOrderMenuService.getSortOrderMenus(
        searchContainer.currentPathView.search_view.sort_order_menus);
      if (!paramsSetFromFocus && searchContainer.orderIsDefault && sortOrderMenus) {
        searchContainer.setOrder(sortOrderMenus[0].order, true);
      }
      searchContainer.selections.allSelected = false;
      setTimeout(() => {
        this.runSearchAfterPathViewChange(searchContainer, keepFilterGroups).then(() => {
          this.searchFilterService.setDefaultCheckedFilters(searchContainer);
        });
      }, 1000);
    }
  }


  async goSearchMenu(menu: any, searchContainer: SearchContainer) {
    searchContainer.targetId = menu.targetId;
    searchContainer.objectCount = menu.objectCount;
    await this.goPathView(menu.path, searchContainer);
  }

  getSearchMenuPath(searchContainer: SearchContainer): SearchMenuPath[] {
    let pathView = searchContainer.currentPathView;
    const pathNames = this.getPath(searchContainer).split('/');
    const res = [];
    let path = '', pathSplit = '';
    pathNames.forEach((pathName, index) => {
      path += pathSplit + pathName;
      pathView = this.getPathView(path, searchContainer);
      if (pathView) {
        const targetId = pathName === 'folder' ? searchContainer.targetId : 'none';
        const menuPath = {
          title: pathView.search_view.title,
          targetId: targetId,
          path: null
        } as SearchMenuPath;
        if (index + 1 < pathNames.length) {
          menuPath.path = path;
        }
        res.push(menuPath);
        pathSplit = '/';
      }
    });
    return res;
  }

  setSearchItemIndex(searchContainer: SearchContainer, index: number) {
    searchContainer.searchItemIndex = index;
  }

  private async runSearchAfterPathViewChange(searchContainer: SearchContainer, keepFilterGroups: boolean = false): Promise<void> {
    if (!keepFilterGroups || !searchContainer.filtersFacets.filterGroups) {
      this.searchViewFilterService.setCheckFilterGroupsFromSearchView(searchContainer);
    }
    await this.searchExecutorService.runSearch(searchContainer);
    this.searchFilterService.checkMenusFromCheckedFilters(searchContainer);
    await this.searchFilterService.setCheckFilterGroups(searchContainer, false, true);
    if (searchContainer.focus.curFocusId) {
      this.searchFilterService.setTotalSelectedFilters(searchContainer);
      this.setCurrentFocusName(searchContainer);
    }
  }

  private checkSetParamsFromFocus(path: string, searchContainer: SearchContainer): boolean {
    let paramsSetFromFocus = false
    const curFocusId = searchContainer.focus.curFocusId;
    if (curFocusId) {
      const pathParams = this.fsi.getSetFocusPathParams(curFocusId, path);
      if (pathParams) {
        this.fsi.setFocusPathParamsOnSearchContainer(pathParams);
        paramsSetFromFocus = true;
      }
    }
    return paramsSetFromFocus;
  }

  private async finishSearchContainerCreation(searchContainer: SearchContainer,
                                              params: SearchContainerParams): Promise<SearchContainer> {
    this.setSearchResultViewDefaultRows(searchContainer);
    if (searchContainer.focus.curFocusId) {
      this.setCurrentFocusName(searchContainer);
    }
    const pathView = await this.setCurrentPathView(searchContainer);
    this.searchFilterService.setCheckedFiltersFromSearchContainer(searchContainer,
      !!pathView, params.defaultCheckedFilters);

    if (params.runSearch) {
      await this.searchExecutorService.runSearch(searchContainer);
      this.searchFilterService.setDefaultCheckedFilters(searchContainer);
      return searchContainer;
    } else {
      return searchContainer;
    }
  }

  private findPathView(pathName: string, searchView: RootSearchView): SearchViewPath {
    let res: SearchViewPath;
    if (searchView.paths) {
      for (const path of searchView.paths) {
        if (path.path_name === pathName) {
          res = this.commons.copy(path);
          break;
        }
      }
    } else {
      console.warn('No paths!');
    }
    return res;
  }

  private getFolderDate(searchContainer: SearchContainer) {
    searchContainer.targetObject['date'] = searchContainer.targetObject.created_at;
    return searchContainer;
  }

  private getPath(searchContainer: SearchContainer): string {
    if (!searchContainer.path) {
      searchContainer.path = 'home';
    }
    return searchContainer.path;
  }

  private async getSearchView(params: SearchContainerParams, filters: any): Promise<RootSearchView> {
    let view = params.searchViewData;
    if (!view) {
      const viewName = params.searchViewName;
      if (!viewName) {
        console.error('Missing view name');
        return view;
      }
      view = await this.userCacheService.getSearchView(viewName);
    }
    if (view) {
      this.setSearchViewFilters(view, filters);
    }
    return view;
  }

  private setSearchViewFilters(view: RootSearchView, filters: any) {
    if (!(filters && Object.keys(filters).length)) {
      return;
    }
    for (const viewPath of view.paths) {
      viewPath.search_view.$$origFilters = viewPath.search_view.$$origFilters || viewPath.search_view.filters ||
        new SearchFilters();
      viewPath.search_view.filters = {...viewPath.search_view.$$origFilters};
      for (const [filterName, filterValue] of Object.entries(filters)) {
        this.setViewPathFilterValues(viewPath, filterName, filterValue);
      }
    }
  }

  private setViewPathFilterValues(viewPath: SearchViewPath, filterName: string, filterValue: any) {
    let filterValues = Array.isArray(filterValue) ? filterValue : [filterValue];
    let viewFilterValues = viewPath.search_view.filters[filterName];
    if (viewFilterValues) {
      viewFilterValues = Array.isArray(viewFilterValues) ? viewFilterValues : [viewFilterValues];
      viewFilterValues = viewFilterValues.concat(filterValues);
    } else {
      viewFilterValues = filterValues;
    }
    viewPath.search_view.filters[filterName] = viewFilterValues;
  }

  private setCurrentFocusName(searchContainer: SearchContainer) {
    for (const focus of searchContainer.focus.focuses) {
      if (focus.focusId === searchContainer.focus.curFocusId) {
        searchContainer.focus.currentFocusName = focus.focus.name;
      }
    }
  }

  private async setCurrentPathView(searchContainer: SearchContainer): Promise<SearchViewPath> {
    const path = this.getPath(searchContainer);
    const pathView = this.getPathView(path, searchContainer);
    if (pathView) {
      searchContainer.currentPathView = pathView;
      if (searchContainer.extendedSearchParams) {
        searchContainer.extendedSearchParams.superobject_type_ids = pathView.search_view.superobject_types;
      }
      if (pathView.reset_query) {
        searchContainer.queryContainer.query = '';
      }
      await this.queryFieldMenuService.setSelectedQueryFieldMenu(searchContainer);
    }
    await this.searchViewFilterService.loadSetTargetObject(searchContainer);
    if (searchContainer.targetObject &&
      searchContainer.currentPathView.search_view.search_view_type === 'folder') {
      searchContainer.object = {
        artifact_id: searchContainer.targetObject[AConst.CREATED_BY_ID]
      };
      this.getFolderDate(searchContainer);
    }
    return pathView;
  }

  private setDefaultSearchContainerProps(searchContainer: SearchContainer, params: SearchContainerParams) {
    const toState = this.primusRouter.currentState();

    if (params.stateParams && toState &&
      toState.indexOf(params.targetState) === 0) {
      this.searchStateService.setSearchContainerPropsFromStateParams(searchContainer, params.stateParams);
      this.searchFilterService.setTotalSelectedFilters(searchContainer);
    }
  }

  private setSearchContainerFromFocus(fsi: FocusServiceImplementation, params: SearchContainerParams) {
    let curFocus: Focus;
    const curFocusId = params.stateParams['cur_focus_id'];
    if (curFocusId) {
      curFocus = fsi.setCurrentFocus(curFocusId);
    } else if (params.useFocus) {
      curFocus = fsi.getCurrentOrDefaultFocus();
      fsi.setCurrentFocus(curFocus);
    }
    if (curFocus) {
      fsi.setFocusPathParamsOnSearchContainer(curFocus.focus.pathParams[curFocus.focus.homePath]);
    }
    return curFocus;
  }

  private setSearchResultViewDefaultRows(searchContainer: SearchContainer) {
    for (const searchResultView of searchContainer.searchView.search_result_views.views) {
      const rowsAndColumns = this.resultViewService.getDefaultCalculatedRowsColumns(searchResultView.name);
      if (rowsAndColumns) {
        searchContainer.rows[searchResultView.name] = rowsAndColumns.rows;
      }
    }
  }

  // Generates a Solr childfield name for ordering based on parent object type, which is actually child object type
  // E.g. if orderFieldName is 'motif_named.named_id_value' and parent_object_type is 'MotifNamedItem', then we
  // need to generate the following sort field using Solr's childfield function:
  // childfield(motif_named.named_id_value,$parent_fq) desc
  // Also need to generate and the parent_fq part, which will be e.g.:
  // parent_fq={!parent which=parent_meta_type:artifact}object_type:MotifNamedItem
  // The "parent which" part must be obtained from searchContainer.currentPathView.search_view.filters.
  private getChildOrderFieldName(searchContainer: SearchContainer, childObjectType: string, orderFieldName: string): [string, string] {
    const parentFilter = this.getParentFilterFromSearchContainer(searchContainer);
    if (parentFilter) {
      const parentFq = `{!parent which=${parentFilter}}` +
        `object_type:${childObjectType}`;
      const childFieldName = `childfield(${orderFieldName},$parent_fq)`;
      return [childFieldName, parentFq];
    } else {
      console.warn('Could not determine parent filter for ordering on child field ' +
        orderFieldName + ' with parent object type ' + childObjectType);
      return [orderFieldName, null];
    }
  }

  // Get parent filter from .currentPathView.search_view.filters. If there is a "parent_meta_type" filter, then that
  // must be used. Else if there is a "meta_type" filter, then that must be used. Else if there is an object_type
  // filter, then that must be used. The filter value may be a string or an array, in which case the first value is used.
  // The filter is returned as a string with the format "<type_filter>:<value>".
  private getParentFilterFromSearchContainer(searchContainer: SearchContainer): string {
    let parentFilter: string;
    const filters = searchContainer.currentPathView.search_view.filters;
    const parentMetaTypeFilter = filters['parent_meta_type'];
    const metaTypeFilter = filters['meta_type'];
    const objectTypeFilter = filters['object_type'];
    if (parentMetaTypeFilter) {
      const filterValue = Array.isArray(parentMetaTypeFilter) ? parentMetaTypeFilter[0] : parentMetaTypeFilter;
      parentFilter = `parent_meta_type:${filterValue}`;
    } else if (metaTypeFilter) {
      const filterValue = Array.isArray(metaTypeFilter) ? metaTypeFilter[0] : metaTypeFilter;
      parentFilter = `meta_type:${filterValue}`;
    } else if (objectTypeFilter) {
      const filterValue = Array.isArray(objectTypeFilter) ? objectTypeFilter[0] : objectTypeFilter;
      parentFilter = `object_type:${filterValue}`;
    }
    return parentFilter;
  }
}
